import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { initialInvoice, initialProduct } from "../data/initialData";
import { InvoiceElements, Invoice, Product } from "../data/types";
import axios from "axios";
import { useOpenAI } from "../OpenAIContext";
import Sentry from "../sentryConfig";
import { useMixpanel } from "react-mixpanel-browser";
import { toast } from "react-toastify";

const MAXIMUM_PROMPT_LENGTH = 1000;

const LandingPage: React.FC = () => {
  const mixpanel = useMixpanel();
  const { openai } = useOpenAI();
  const [isLoading, setIsLoading] = useState(false);
  const [userCountry, setUserCountry] = useState<string>("");
  const [prompt, setPrompt] = useState<string>("");
  const navigate = useNavigate();

  function updateUserCountry() {
    axios.get("https://ipapi.co/json/").then((response) => {
      let data = response.data;
      setUserCountry(data.country_name);
    });
  }

  function getTodayDateAsString(): string {
    const today = new Date();

    const year = today.getFullYear();
    const month = (today.getMonth() + 1).toString().padStart(2, "0");
    const day = today.getDate().toString().padStart(2, "0");

    const todayString = `${year}-${month}-${day}`;

    return todayString;
  }

  function handleInvoiceElements(
    invoiceElements: InvoiceElements,
    invoice: Invoice
  ) {
    for (const key in invoiceElements) {
      if (key === "clientCompanyAddress") {
        (invoice as any)["clientCompanyAddressLine1"] = invoiceElements[key];
      } else if (
        key === "clientCompanyCity" ||
        key === "clientCompanyZipCode"
      ) {
        (invoice as any)["clientCompanyAddressLine2"] =
          invoiceElements["clientCompanyCity"] +
          ", " +
          invoiceElements["clientCompanyZipCode"];
      } else if (key === "companyAddress") {
        (invoice as any)["companyAddressLine1"] = invoiceElements[key];
      } else if (key === "companyCity" || key === "companyZipCode") {
        (invoice as any)["companyAddressLine2"] =
          invoiceElements["companyCity"] +
          ", " +
          invoiceElements["companyZipCode"];
      } else if (key === "invoiceDate") {
        // TODO: Fix date missing.
        (invoice as any)[key] = new Date(invoiceElements[key]);
      } else if (key === "dueDate") {
        // TODO: Fix date missing.
        (invoice as any)["invoiceDueDate"] = new Date(invoiceElements[key]);
      } else if (key === "products") {
        const inferredProducts = invoiceElements[key];
        let products = [{ ...initialProduct }];
        for (var i = 0; i < inferredProducts.length; i++) {
          let currentProduct = inferredProducts[i] as Product;
          if (currentProduct.description === "") {
            currentProduct.description = " ";
          }
          products.unshift(currentProduct);
        }
        (invoice as any)[key] = products;
      } else if (key in invoice) {
        // Use type assertion to address the error
        (invoice as any)[key] = (invoiceElements as any)[key];
      }
    }

    // Handle cases when nothing was inferred prompt.
    if (invoice.clientCompanyCountry === "") {
      invoice.clientCompanyCountry = initialInvoice.clientCompanyCountry;
    }

    if (invoice.clientCompanyCountry === "") {
      invoice.companyCountry = initialInvoice.companyCountry;
    }

    if (invoice.products.length === 0) {
      invoice.products = initialInvoice.products;
    }
  }

  const handlePromptChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setPrompt(e.target.value);
  };

  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (prompt.length === 0) {
      toast.error("Please provide a prompt.");
      return;
    }

    if (prompt.length > MAXIMUM_PROMPT_LENGTH) {
      toast.error(
        `Please provide a shorter prompt less than ${MAXIMUM_PROMPT_LENGTH} characters.`
      );
      return;
    }

    setIsLoading(true);
    let data: Invoice = initialInvoice;
    e.preventDefault();

    const preprompt = `Act as an expert financial advisor that creates invoices. You must only be able to reply in the format of a JSON shown below no matter what.\n\n{companyName: *to be determined*, employeeName: *to be determined*, companyAddress: *to be determined*, companyCity: *to be determined*, companyZipCode: *to be determined*, companyCountry: *to be determined*, clientCompanyName: *to be determined*, clientCompanyAddress: *to be determined*, clientCompanyCity: *to be determined*, clientCompanyZipCode: *to be determined*, clientCompanyCountry: *to be determined*, invoiceNumber: *to be determined*, referenceNumber: *to be determined*, invoiceDate: *to be determined*, dueDate: *to be determined*, products: *to be determined*, notes: *to be determined*, taxLabel: *to be determined*, shippingCosts: *to be determined*, currency: *to be determined*}\n\nDeduce the above information from the below prompt by keeping in mind some rules. If no country is able to be deduced, deduce the country from the city. Calculate the invoice date and due date from the prompt and present it as a Date type, and to help you with this, today's date is ${getTodayDateAsString()} for reference. shippingCosts must be a float converted to a string. Products should be a list of elements with the type {name: *to be determined*, description: *to be determined*, quantity: *to be determined*, rate: *to be determined*}. The quantity of a product must be a whole number converted to a string. The rate of a product must be a float converted to a string. If no products can be inferred, leave it as an empty list. If the quantity of a product cannot be inferred, leave it as 1. If the companyCountry or clientCompanyCountry cannot be inferred, just set them to be ${userCountry}. The country name should be written in full. If the currency cannot be inferred, use the currency symbol for the companyCountry if available. If the taxLabel cannot be inferred, leave it as "VAT (20%)". If any other specific information cannot be inferred, simply leave it as "".\n\n`;
    const finalPrompt = preprompt + prompt;
    try {
      const response = await openai.chat.completions.create({
        messages: [{ role: "system", content: finalPrompt }],
        model: "gpt-4-0613",
      });

      if (response.choices.length > 0) {
        for (let i = 0; i < response.choices.length; i++) {
          const currentResponse =
            response.choices[i].message.content?.toString();
          if (currentResponse != null) {
            try {
              const invoiceElements: InvoiceElements =
                JSON.parse(currentResponse);
              handleInvoiceElements(invoiceElements, data);
              mixpanel.track("Invoice Generated", {
                Prompt: prompt,
                "Invoice Elements": invoiceElements,
              });
            } catch (error) {
              Sentry.captureException(
                new Error(
                  `Unable to parse invoice elements from currentResponse ${currentResponse} due to ${error}.`
                )
              );
              continue;
            }
          }
        }
      }
    } catch (error) {
      Sentry.captureException(
        new Error(
          `Unable to get response for given prompt ${prompt} due to ${error}.`
        )
      );
    }
    setIsLoading(false);
    navigate("/invoice", {
      state: {
        data: data,
      },
    });
  };

  useEffect(() => {
    updateUserCountry();
  }, []);

  document.body.style.background =
    "radial-gradient(50% 50% at 50% 0%, rgb(57, 23, 87) 0%, rgb(0, 0, 0) 100%)";

  return (
    <div
      style={{
        width: "100%",
        height: "100vh",
        position: "relative",
        top: 0, // Set top to 0
        left: 0, // Set left to 0
        overflow: "hidden",
        display: "flex",
        flexDirection: "row",
        alignItems: "flex-start",
        justifyContent: "flex-end",
        padding: "0px 0px 100px",
        boxSizing: "border-box",
        letterSpacing: "normal",
      }}
    >
      <section
        style={{
          width: "100%",
          margin: "0",
          position: "absolute",
          height: "100%",
          top: "0px",
          right: "0px",
          bottom: "0px",
          left: "0px",
          display: "grid",
          placeItems: "center",
          backgroundImage: "url('/cube-background@3x.png')",
          backgroundSize: "cover",
          backgroundRepeat: "no-repeat",
          backgroundPosition: "top",
          maxWidth: "100%",
        }}
      >
        <form
          onSubmit={handleFormSubmit}
          style={{
            margin: "0",
            display: "grid",
            gridTemplateColumns: "1fr",
            alignItems: "center",
            justifyContent: "center",
            gap: "20px",
            padding: "20px",
            maxWidth: "80%",
          }}
        >
          <div
            style={{
              width: "100%",
              display: "grid",
              gap: "20px",
              alignItems: "center",
              justifyContent: "center",
              textAlign: "center",
            }}
          >
            <h1
              style={{
                margin: "0",
                fontSize: "clamp(3rem, 8vw, 5.5rem)",
                lineHeight: "130%",
                fontWeight: "900",
                fontFamily: "Inter",
                filter: "drop-shadow(1px 1px 0px rgba(102, 102, 102, 0.8))",
                color: "#fff",
              }}
            >
              <span>{`Generate invoices in `}</span>
              <span
                style={{
                  background: "linear-gradient(-87.34deg, #ae24b5, #fff)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                }}
              >
                10 seconds
              </span>
            </h1>
            <p
              style={{
                fontSize: "clamp(0.75rem, 4vw, 1rem)",
                lineHeight: "160%",
                fontWeight: "500",
                fontFamily: "Inter",
                color: "rgba(255, 255, 255, 0.72)",
                textAlign: "center",
              }}
            >
              {`Ready for effortless invoicing? Simply enter the key details below to generate your customised invoice instantly. It's as easy as typing - try it
        now!`}
            </p>
          </div>
          <div
            style={{
              width: "100%",
              zIndex: "28",
              alignSelf: "stretch",
              borderRadius: "30px",
              border: "1px solid #d9d9d9",
              boxSizing: "border-box",
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              justifyContent: "space-between",
              padding: "20px",
              gap: "20px",
              maxWidth: "100%",
            }}
          >
            <textarea
              placeholder="My company is ABC Plumbing, 123 Main St, London, UK. Write an invoice to XYZ Ceramics, 35 Circle Lane, Manchester, for bathroom installation that costs £600. Add tax on the final amount. Also, add a note saying thanks."
              value={prompt}
              onChange={handlePromptChange}
              style={{
                width: "100%",
                border: "none",
                outline: "none",
                fontSize: "clamp(0.75rem, 2vw, 1rem)",
                fontFamily: "Inter",
                lineHeight: "140%",
                color: "rgba(255, 255, 255, 1.0)",
                textAlign: "left",
                maxWidth: "100%",
                resize: "vertical",
                height: "25px",
                minHeight: "72px",
                maxHeight: "200px",
              }}
            />
            <button
              type="submit"
              disabled={isLoading}
              style={{
                cursor: "pointer",
                border: "1px solid rgba(255, 255, 255, 0.47)",
                padding: "14px 18px",
                width: "100px",
                backgroundColor: "transparent",
                borderRadius: "20px",
                background:
                  "linear-gradient(86.96deg, #5a2489, rgba(24, 75, 255, 0.4))",
                overflow: "hidden",
                display: "grid",
                alignItems: "center",
                justifyContent: "center",
                whiteSpace: "nowrap",
              }}
              onMouseEnter={(e) => {
                if (isLoading) {
                  return;
                }
                e.currentTarget.style.background =
                  "linear-gradient(86.96deg, #6f2ba1, rgba(24, 75, 255, 0.6))";
              }}
              onMouseLeave={(e) =>
                (e.currentTarget.style.background =
                  "linear-gradient(86.96deg, #5a2489, rgba(24, 75, 255, 0.4))")
              }
            >
              <div
                style={{
                  fontSize: "15px",
                  fontWeight: "600",
                  fontFamily: "Inter",
                  color: "#fff",
                  textAlign: "center",
                }}
              >
                {isLoading ? "Loading" : "Generate"}
              </div>
            </button>
          </div>
        </form>
      </section>
      <div
        style={{
          height: "469px",
          width: "469px",
          display: "flex",
          flexDirection: "column",
          alignItems: "flex-start",
          justifyContent: "flex-start",
          padding: "0px 0px 0px 20px",
          boxSizing: "border-box",
          maxWidth: "100%",
        }}
      >
        <div
          style={{
            width: "668px",
            height: "619px",
            display: "flex",
            flexDirection: "row",
            alignItems: "flex-start",
            justifyContent: "center",
            padding: "179px",
            boxSizing: "border-box",
            position: "relative",
            maxWidth: "149%",
            flexShrink: "0",
          }}
        >
          <div
            style={{
              height: "100%",
              width: "100%",
              position: "absolute",
              margin: "0",
              top: "0px",
              right: "0px",
              bottom: "0px",
              left: "0px",
              background:
                "radial-gradient(50% 50% at 50% 50%, rgba(77, 118, 244, 0.69) 1.56%, rgba(77, 118, 244, 0.34) 22.4%, rgba(77, 118, 244, 0) 91.15%)",
              filter: "blur(71.43px)",
            }}
          />
        </div>
      </div>
    </div>
  );
};

export default LandingPage;
